import { CaretLeftIcon } from '@radix-ui/react-icons';
import { Avatar, Badge, Skeleton, TabNav } from '@radix-ui/themes';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useAppNavigation } from 'helpers/useAppNavigation';
import { useWindowSize } from 'helpers/useWindowSize';
import type { ClubPageType } from 'types/club';
import FollowUnfollowButton from './FollowUnfollowButton';
import MembershipLabel from './MembershipLabel';
import MessageDialog from './MessageDialog';
import PlayerObjectSkeleton from './PlayerObjectSkeleton';

export const ClubHeader: React.FC<Partial<ClubPageType> & { loading: boolean }> = ({
  id,
  name,
  active,
  followed,
  followable_type,
  managed,
  club_manager_profile_id,
  thumbnail,
  loading,
}) => {
  const { navigateBack } = useAppNavigation();
  const size = useWindowSize();
  const { ref, inView } = useInView({ threshold: size.width > 768 ? 1 : 0.4 });
  const subMenuSticky = size.height > 600;

  const activeTab = () => {
    if (window.location.pathname.includes('membri')) return 1;
    else if (window.location.pathname.includes('competizioni')) return 2;
    else if (window.location.pathname.includes('social')) return 3;
    else return 0;
  };

  const urlPath = `/circolo/${id}`;

  const subMenu = (
    <div
      id='submenu'
      className={classNames(
        { 'sticky z-[9] md:top-[calc(11rem-1px)]': subMenuSticky },
        { 'top-32': subMenuSticky && !Capacitor.isNativePlatform() },
        { 'top-40': subMenuSticky && Capacitor.isNativePlatform() },
      )}
    >
      <TabNav.Root className='flex justify-center rounded-b-xl bg-neutral-neutral-2 md:gap-4'>
        <TabNav.Link asChild active={activeTab() === 0}>
          <Link to={id ? urlPath : '#'}>Panoramica</Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 1}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/membri` : '#'}>
            Membri
          </Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 2}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/competizioni` : '#'}>
            Competizioni
          </Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 3}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/social` : '#'}>
            Social
          </Link>
        </TabNav.Link>
      </TabNav.Root>
    </div>
  );

  const buttons = (
    <div id='buttons' className='flex items-center justify-center gap-2 self-stretch p-2 md:items-end md:justify-end'>
      <div id='segui'>
        <FollowUnfollowButton id={id!} type={followable_type!} followed={followed!} />
      </div>
      <div id='message'>
        {managed && (
          <MessageDialog
            name={name!}
            id={club_manager_profile_id!}
            thumbnail={thumbnail!}
            recipient_type='club_manager_profile'
          />
        )}
      </div>
      {/* DISABLED FOR NOW - 3 DOT MENU ON PLAYER
      <div>
        <IconButton variant='outline' className='hover:cursor-pointer'>
          <DotsVerticalIcon width='18' height='18' />
        </IconButton>
      </div> */}
    </div>
  );

  const miniHeader = (
    <div
      className={classNames(
        { 'sticky z-[9] md:top-[calc(8rem-1px)]': subMenuSticky },
        { 'top-20': subMenuSticky && !Capacitor.isNativePlatform() },
        { 'top-[7.5rem]': subMenuSticky && Capacitor.isNativePlatform() },
        'flex flex-row items-center justify-center gap-2 rounded-t-xl bg-neutral-neutral-1 p-1',
      )}
    >
      <div
        className='flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid border-neutral-neutral-11'
        onClick={navigateBack}
      >
        <CaretLeftIcon className='h-6 w-6 text-neutral-neutral-11' />
      </div>
      <Avatar
        alt='Avatar Club'
        className='rounded-xl'
        src={thumbnail!}
        fallback={<img src='/images/tennisclub-avatar.jpg'></img>}
      />

      <div className='min-w-0 flex-1'>
        <div
          className={classNames('mr-1 flex items-center gap-2 truncate font-bold', {
            'text-neutral-neutral-7': active === false,
          })}
        >
          {name}
        </div>
      </div>
    </div>
  );

  if (loading || !name) {
    return (
      <>
        <div className='flex flex-col items-start gap-2 rounded-t-xl bg-neutral-neutral-1 p-3 md:flex-row md:items-end'>
          <PlayerObjectSkeleton bigHeader={true} />
          <Skeleton className='h-8 w-16' />
        </div>
        {subMenu}
      </>
    );
  }

  return (
    <>
      <div className='flex flex-col items-start gap-2 overflow-hidden rounded-t-xl bg-neutral-neutral-1 p-3' ref={ref}>
        {size.width < 1024 && (
          <div
            className='flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid border-neutral-neutral-11'
            onClick={navigateBack}
          >
            <CaretLeftIcon className='h-6 w-6 text-neutral-neutral-11' />
          </div>
        )}
        <div id='club' className='flex w-full items-start gap-2 self-stretch p-2'>
          <Avatar
            alt='Avatar Club'
            className='flex h-24 w-24 rounded-xl'
            src={thumbnail!}
            fallback={<img src='/images/tennisclub-avatar.jpg'></img>}
          />

          <div
            className={classNames('flex items-center gap-2 self-stretch p-2 text-2xl font-bold', {
              'text-neutral-neutral-7': active === false,
            })}
          >
            {name}
            {managed && (
              <div id='membership' className=''>
                <MembershipLabel premiumMember={false} premiumClubMember={false} />
              </div>
            )}
          </div>
        </div>
        {active === false && (
          <div className='flex items-baseline gap-2 pb-2'>
            <Badge size='3' variant='soft' color='gray' className='text-sm text-neutral-neutral-12'>
              Club non più attivo
            </Badge>
          </div>
        )}
        {buttons}
      </div>
      {!inView && miniHeader}
      {subMenu}
    </>
  );
};
export default ClubHeader;
